import { TokenDto } from "@/api/generated";
import { useAccounts } from "@/stores/accounts";
import { storeToRefs } from "pinia";
import { ref, shallowRef, watch } from "vue";
import { useAeppSdk } from "./aeppSdk";
import BigNumber from "bignumber.js";
import { Decimal } from "@/libs/decimal";
import {
  BondingCurveTokenSale,
  initAffiliationTokenGatingTokenSale,
  toTokenDecimals,
} from "bctsl-sdk";
import {
  AeSdkBase,
  Contract,
  ContractMethodsBase,
  Encoded,
  toAe,
} from "@aeternity/aepp-sdk";
import { useQueryClient } from "@tanstack/vue-query";
import { useI18n } from "vue-i18n";
import { TRANSACTION_STATES, TRANSACTION_TYPES } from "@/utils/constants";
import { useTokenTradeStore } from "@/stores/tokenTradeStore";
import { useTransactionConfirmModal } from "@/stores/transactionConfirmModal";

/**
 * Bonding Curve Price Calculation methods inspired from the contract implementation
 */

// Constants
const k = new BigNumber(2).multipliedBy(10 ** 7); // Scaling factor (2 * 10^7)
const initialPrice = new BigNumber(1).multipliedBy(10 ** 8); // Initial price (1 * 10^8)
const decimals = new BigNumber(10).pow(18); // Precision scaling (10^18)
const minimumCount = new BigNumber(10).pow(12); // Minimum count (10^12)
const sellReturnPercentage = new BigNumber(9950); // Sell return percentage (99.5%)
const sellReturnPrecision = new BigNumber(10000); // Precision for percentage calculations

// Maclaurin Expansion for e^x (approximates e^x iteratively, starting at the quadratic term)
function mc2(x: BigNumber): BigNumber {
  return expHelper(x, x, new BigNumber(0), new BigNumber(2), decimals.pow(2));
}

function expHelper(
  x: BigNumber,
  term: BigNumber,
  sum: BigNumber,
  i: BigNumber,
  scale: BigNumber
): BigNumber {
  if (i.gt(100) || term.lt(1)) {
    return sum;
  }
  const nextTerm = term.multipliedBy(x).dividedBy(i.multipliedBy(scale));
  return expHelper(x, nextTerm, sum.plus(nextTerm), i.plus(1), scale);
}

// Calculate Price Function
function calculatePrice(totalSupply: BigNumber, count: BigNumber): BigNumber {
  if (count.lt(minimumCount)) {
    throw new Error("MINIMUM_TOKENS_REQUIRED");
  }

  const currentSupplyExponent = k.multipliedBy(totalSupply);
  const nextSupplyExponent = k.multipliedBy(totalSupply.plus(count));

  const currentValue = mc2(currentSupplyExponent);
  const nextValue = mc2(nextSupplyExponent);

  const linearComponent = initialPrice.multipliedBy(count).dividedBy(decimals);
  const exponentialComponent = nextValue.minus(currentValue).dividedBy(k);

  return linearComponent.plus(exponentialComponent);
}

// Calculate Buy Price
function calculateBuyPrice(
  totalSupply: BigNumber,
  count: BigNumber
): BigNumber {
  return calculatePrice(totalSupply, count);
}

// Calculate Sell Return
function calculateSellReturn(
  totalSupply: BigNumber,
  sellTokens: BigNumber
): BigNumber {
  const price = calculatePrice(totalSupply.minus(sellTokens), sellTokens);
  return price
    .multipliedBy(sellReturnPercentage)
    .dividedBy(sellReturnPrecision);
}

// Calculate Buy Price with Affiliation Fee 0.005%
function calculateBuyPriceWithAffiliationFee(
  totalSupply: BigNumber,
  count: BigNumber
): BigNumber {
  return calculateBuyPrice(totalSupply, count).multipliedBy(1.005);
}

// Helper
const toDecimals = (amount: any, decimals: any) =>
  new BigNumber(amount).multipliedBy(new BigNumber(10).pow(decimals || 18));

/**
 * Bonding Curve Price Calculation methods inspired from the backend
 */
function calculateTokensFromAE(totalSupply: string, amount: string): number {
  const calculators = {
    default: (targetValue: any, supply: any) => {
      // Define the integral function from supply to supply + x
      function integralFunction(x: any) {
        return (
          Math.exp(0.00000000002 * (supply + x)) / 0.00000000002 -
          Math.exp(0.00000000002 * supply) / 0.00000000002 -
          1 * x -
          targetValue
        );
      }

      // Set an initial guess dynamically based on the target
      const initialGuess = Math.max(1000000, targetValue * 1000);

      // Define the tolerance and the maximum number of iterations
      const tolerance = 1e-5;
      const maxIterations = 10000;

      // Define the bisection method
      let low = 0; // Set a lower bound
      let high = initialGuess; // Set an upper bound
      let x = (low + high) / 2; // Midpoint between low and high
      let iteration = 0;
      let error = Math.abs(integralFunction(x));

      // Perform bisection method
      while (error > tolerance && iteration < maxIterations) {
        // Check if the function changes signs
        if (integralFunction(low) * integralFunction(x) < 0) {
          high = x; // If sign changes, move the high bound
          x = (low + high) / 2; // New midpoint
        } else {
          console.log("low", low, low + high / 2);
          low = x; // If no sign change, move the low bound
          x = low + high / 4; // New midpoint
        }
        error = Math.abs(integralFunction(x)); // Update error
        iteration++;
      }

      return x;
    },
  };

  const findXForTarget = calculators.default;

  const x = findXForTarget(
    amount,
    new BigNumber(totalSupply).shiftedBy(-18).toNumber()
  );

  return x;
}

export interface UseTokenTradeProps {
  token: TokenDto;
}

const tokenSaleInstance = shallowRef<BondingCurveTokenSale>();
const bondingCurveInstance = shallowRef<Contract<ContractMethodsBase>>();

export function useTokenTrade({ token }: UseTokenTradeProps) {
  const {
    tokenA,
    tokenB,
    tokenAFocused,
    token: tokenStore,
    activeTradeView,
    isBuying,
    isAllowSelling,
    loadingTransaction,
    nextPrice,
    userBalance,
    desiredSlippage,
  } = storeToRefs(useTokenTradeStore());
  const transactionConfirmModal = useTransactionConfirmModal();

  const queryClient = useQueryClient();
  const { t } = useI18n();

  const errorMessage = ref();
  const { activeAccount, slippage } = storeToRefs(useAccounts());
  const { staticAeSdk, getAeSdk } = useAeppSdk();

  const currentState = ref<TRANSACTION_STATES>(
    staticAeSdk
      ? TRANSACTION_STATES.CONNECTED
      : TRANSACTION_STATES.NOT_CONNECTED
  );

  async function resetFormState() {
    currentState.value = TRANSACTION_STATES.CONNECTED;
    nextPrice.value = Decimal.ZERO;
  }

  async function buy() {
    errorMessage.value = undefined;
    if (!tokenSaleInstance.value) {
      errorMessage.value = t("blocks.tokenTradeCard.contractNotInitialized");
      return;
    }

    if (!tokenB.value) {
      errorMessage.value = t("blocks.tokenTradeCard.amountNotSet");
      return;
    }

    loadingTransaction.value = true;
    try {
      await tokenSaleInstance.value.buy(
        tokenB.value,
        undefined,
        slippage.value
      );
      await onTransactionComplete();
      currentState.value = TRANSACTION_STATES.SUCCESS;
    } catch (error: any) {
      errorMessage.value = error?.message;
    }
    loadingTransaction.value = false;
  }

  async function sell() {
    errorMessage.value = null;
    if (!tokenSaleInstance.value) {
      errorMessage.value = t("blocks.tokenTradeCard.contractNotInitialized");
      return;
    }

    if (!tokenA.value) {
      errorMessage.value = t("blocks.tokenTradeCard.amountNotSet");
      return;
    }

    loadingTransaction.value = true;
    try {
      isAllowSelling.value = true;

      const countTokenDecimals =
        await tokenSaleInstance.value.createSellAllowance(
          tokenA.value?.toString()
        );

      isAllowSelling.value = false;

      await tokenSaleInstance.value.sellWithExistingAllowance(
        countTokenDecimals,
        slippage.value
      );

      await onTransactionComplete();

      currentState.value = TRANSACTION_STATES.SUCCESS;
    } catch (error: any) {
      errorMessage.value = error?.message;
    }
    loadingTransaction.value = false;
  }

  async function onTransactionComplete() {
    queryClient.invalidateQueries({
      queryKey: ["TokensService.findByAddress", token.sale_address],
    });
    queryClient.invalidateQueries({
      queryKey: ["MiddlewareService.getTxsByScope", token.sale_address],
    });
    await fetchUserTokenBalance();
    // await fetchTotalSupply();
  }

  async function fetchUserTokenBalance() {
    if (!tokenSaleInstance.value) {
      throw new Error(t("blocks.tokenTradeCard.contractNotInitialized"));
    }

    const tokenContract = await tokenSaleInstance.value.tokenContractInstance();
    if (activeAccount.value) {
      const result: { decodedResult?: bigint } = await tokenContract.balance(
        activeAccount.value
      );

      if (!result) throw new Error("User balance not fetched");
      const tokenBalance = result.decodedResult || 0n;

      if (!token) throw new Error("metaInfo not fetched");

      userBalance.value = toTokenDecimals(
        tokenBalance,
        // 18n,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        token.decimals || 18n,
        0n
      );
    }
  }

  async function setupContractInstance(sdk: AeSdkBase) {
    tokenSaleInstance.value = await initAffiliationTokenGatingTokenSale(
      sdk,
      token.sale_address as Encoded.ContractAddress
    );

    bondingCurveInstance.value = (
      await tokenSaleInstance.value.bondingCurveContract()
    ).instance;

    if (activeAccount.value) {
      await fetchUserTokenBalance();
    }
  }

  async function onConfirm() {
    transactionConfirmModal.setTransactionType(TRANSACTION_TYPES.TRADE);
    desiredSlippage.value = slippage.value;
    tokenStore.value = token;
    if (isBuying.value) {
      buy().finally(() => {
        transactionConfirmModal.clearData();
      });
    } else {
      sell().finally(() => {
        transactionConfirmModal.clearData();
      });
    }
  }

  watch(
    activeTradeView,
    () => {
      if (currentState.value === TRANSACTION_STATES.SUCCESS) {
        resetFormState();
      }
    },
    {
      immediate: true,
    }
  );

  watch(
    () => token,
    async (_token, oldToken) => {
      if (!_token) return;
      if (_token.address !== oldToken?.address) {
        resetFormState();
      }
      tokenStore.value = _token;
    },
    {
      immediate: true,
    }
  );

  // Retrieve the next price using bonding curve instance
  function calculateNextPrice(currentSupply: Decimal) {
    try {
      nextPrice.value = Decimal.from(
        toAe(
          calculateBuyPrice(
            new BigNumber(currentSupply.bigNumber),
            new BigNumber(1).multipliedBy(new BigNumber(10).pow(18))
          )
        )
      );
    } catch (error) {
      console.error("Error calculating bonding curve price:", error);
      nextPrice.value = Decimal.from(0);
    }
  }

  function calculateTokenCost(
    amount?: number,
    _isBuying = false,
    _isUsingToken = false
  ) {
    const tokenDecimals = token.decimals ?? 18;
    const tokenSupply = new BigNumber(token.total_supply ?? 0);
    let currentSupply = Decimal.from(toAe(tokenSupply.toString()));

    if (!amount || amount <= 0) {
      calculateNextPrice(currentSupply);
      return amount;
    }

    const tokenAmountBigNumber = new BigNumber(
      toDecimals(amount, tokenDecimals).toString()
    );
    let tokenAmountCostDecimal: Decimal;

    if (_isBuying) {
      if (_isUsingToken) {
        currentSupply = currentSupply.add(Decimal.from(amount));
        tokenAmountCostDecimal = Decimal.from(
          toAe(
            calculateBuyPriceWithAffiliationFee(
              tokenSupply,
              tokenAmountBigNumber
            )
          )
        );
      } else {
        tokenAmountCostDecimal = Decimal.from(
          calculateTokensFromAE(
            tokenSupply.toString(),
            amount.toString()
          ).toFixed(2)
        );
        currentSupply = currentSupply.add(tokenAmountCostDecimal.toString());
      }
    } else {
      if (_isUsingToken) {
        currentSupply = currentSupply.sub(Decimal.from(amount));
        tokenAmountCostDecimal = Decimal.from(
          toAe(calculateSellReturn(tokenSupply, tokenAmountBigNumber))
        );
      } else {
        tokenAmountCostDecimal = Decimal.from(
          calculateTokensFromAE(
            tokenSupply.toString(),
            amount.toString()
          ).toFixed(2)
        );
        currentSupply = currentSupply.sub(tokenAmountCostDecimal.toString());
      }
    }

    calculateNextPrice(currentSupply);

    return parseFloat(tokenAmountCostDecimal.toString());
  }

  /**
   * Watch TokenA
   */
  watch(
    [tokenA, isBuying, tokenAFocused],
    async (
      [newTokenA, isBuyingToken, newTokenAFocused],
      [oldTokenA, oldIsBuyingToken]
    ) => {
      if (
        // !newTokenA ||
        // isNaN(Number(newTokenA)) ||
        !tokenSaleInstance.value ||
        !newTokenAFocused ||
        (newTokenA === oldTokenA && isBuyingToken === oldIsBuyingToken)
      ) {
        return;
      }
      tokenB.value = calculateTokenCost(
        newTokenA,
        isBuyingToken,
        !isBuyingToken
      );
    }
  );

  /**
   * Watch TokenB
   */
  watch(
    [tokenB, isBuying, tokenAFocused],
    async (
      [newTokenB, isBuyingToken, newTokenAFocused],
      [oldTokenB, oldIsBuyingToken]
    ) => {
      if (
        // !newTokenB ||
        // isNaN(Number(newTokenB)) ||
        !tokenSaleInstance.value ||
        newTokenAFocused ||
        (newTokenB === oldTokenB && isBuyingToken === oldIsBuyingToken)
      ) {
        return;
      }
      tokenA.value = calculateTokenCost(
        newTokenB,
        !isBuyingToken,
        isBuyingToken
      );
    }
  );

  watch(
    activeAccount,
    async () => {
      if (activeAccount.value) {
        currentState.value = TRANSACTION_STATES.CONNECTED;
        const sdk = await getAeSdk();
        await setupContractInstance(sdk);
      } else {
        currentState.value = TRANSACTION_STATES.NOT_CONNECTED;
        await setupContractInstance(staticAeSdk);
      }
      // void fetchTotalSupply();
    },
    {
      immediate: true,
    }
  );

  return {
    // methods
    resetFormState,
    onConfirm,
    // computed
    currentState,
    errorMessage,
  };
}
